// src/components/scenes/HomeScene.js
// Filepath: // src/components/scenes/HomeScene.js
/* eslint-disable no-console */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useScene } from "../../state/SceneContext";
import { useHomeDataStore } from "../../state/homeDataStore";
import Hero from "../common/Hero";
import SideNav from "../common/SideNav";
import CustomSliderRow from "../common/CustomSliderRow";
import {
  getRememberedCol,
  getLastRowIndex,
  clearRowMemory,
  focusRowCol,
  setSceneHandlers,
} from "../../input/remote";
import { useKeepScreenOn } from "../../hooks/useKeepScreenOn";
import { buildRails } from "../../lib/contentNormalizer";

const NAV_SAFE_LEFT = 24 + 72 + 32;
const ANCHOR_EXTRA_OFFSET = 30;

const HOME_HERO_DEFAULT = {
  title: "Welcome to Kbean TV",
  backgroundUrl: "https://picsum.photos/seed/hero-bg-kbean/1920/1080",
};

const clamp = (n, min, max) =>
  Math.min(Math.max(Number.isFinite(n) ? Math.floor(n) : 0, min), max);

function getSafeRow(rowCount) {
  return clamp(getLastRowIndex() ?? 0, 0, Math.max(0, rowCount - 1));
}

function getSafeColForRow(railsArray, rowIndex) {
  const itemsLen = railsArray?.[rowIndex]?.items?.length ?? 0;
  return clamp(getRememberedCol(rowIndex) ?? 0, 0, Math.max(0, itemsLen - 1));
}

const pickBackground = (item) =>
  item?.heroImage ||
  item?.backdrop ||
  item?.backgroundUrl ||
  item?.artUrl || // free content fallback
  item?.thumbnail ||
  item?.thumbnailUrl ||
  HOME_HERO_DEFAULT.backgroundUrl;

const pickTitle = (item) => item?.title || item?.name || HOME_HERO_DEFAULT.title;
const pickSubtitle = (item) =>
  item?.subtitle ?? item?.description ?? item?.summary ?? item?._raw?.artDescription ?? "";

// ── Hero field pickers (defensive reads from item and item._raw) ──────────
function pickArtistFirst(item) {
  return item?.artistFirstName ?? item?._raw?.artistFirstName ?? item?.artistFirst ?? null;
}
function pickArtistLast(item) {
  return item?.artistLastName ?? item?._raw?.artistLastName ?? item?.artistLast ?? null;
}
function pickYear(item) {
  // Prefer year, then artYear; check both normalized and raw shapes
  return (
    item?.year ??
    item?.artYear ??
    item?._raw?.year ??
    item?._raw?.artYear ??
    null
  );
}
function pickMedium(item) {
  return item?.medium ?? item?._raw?.medium ?? null;
}
function pickMediaType(item) {
  return item?.expectedMediaType ?? item?.type ?? item?._raw?.expectedMediaType ?? null; // "Video" | "Image" | null
}
function pickShares(item) {
  return item?.sharesYouOwn ?? item?._raw?.sharesYouOwn ?? 0;
}
function pickValue(item) {
  return item?.valueYouOwn ?? item?._raw?.valueYouOwn ?? null;
}

/** Normalize for Item Detail: now enriched to match Home hero metadata */
function normalizeForDetail(item) {
  const expectedMediaType = pickMediaType(item);
  const isVideo = expectedMediaType === "Video";

  const af = pickArtistFirst(item);
  const al = pickArtistLast(item);
  const yr = pickYear(item);
  const byline =
    af || al || yr
      ? [[af, al].filter(Boolean).join(" "), yr].filter(Boolean).join(" • ")
      : null;

  return {
    id: item?.id,
    title: item?.title ?? item?.name ?? "",
    subtitle: pickSubtitle(item) || null,
    expectedMediaType,
    isVideo,
    mediaUrl: isVideo ? item?.mediaUrl ?? null : null,
    previewUrl: isVideo ? item?.previewUrl ?? null : null,
    imageUrl: !isVideo ? item?.imageUrl ?? null : null,
    durationSec: item?.durationSec ?? null,
    backgroundUrl: pickBackground(item),
    thumbnailUrl: item?.thumbnailUrl ?? item?.thumbnail ?? null,

    // ── NEW: hero-friendly metadata so ItemDetailScene can render parity UI ──
    byline,                                   // "Artist • Year"
    medium: pickMedium(item) || null,         // e.g., "Oil on canvas"
    sharesYouOwn: Number(pickShares(item) || 0),
    valueYouOwn: pickValue(item),

    _raw: item,
  };
}

/** Debug toggles */
function isDebugApi() {
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("debugApi") === "1") return true;
      if (window.__KBEAN_DEBUG_API === true) return true;
    }
  } catch {}
  return String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";
}
function isDebugItem() {
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("debugItem") === "1") return true;
      if (window.__KBEAN_DEBUG_ITEM === true) return true;
    }
  } catch {}
  return String(process.env.REACT_APP_DEBUG_ITEM || "").toLowerCase().trim() === "true";
}

// Simple "log once per session" guard for dev StrictMode duplicate effects
const logOnce = (() => {
  const seen = new Set();
  return (key, fn) => {
    if (seen.has(key)) return;
    seen.add(key);
    try {
      fn();
    } catch {}
  };
})();

export default function HomeScene() {
  const { scene, setScene, pushScene } = useScene();

  const freeData = useHomeDataStore((s) => s.free.data);
  const holdingsData = useHomeDataStore((s) => s.holdings.data);
  const profileData = useHomeDataStore((s) => s.profile.data);
  const studioItems = useHomeDataStore((s) =>
    (typeof s.selectActivityItems === "function" ? s.selectActivityItems() : []) || []
  );

  const railsBase = useMemo(
    () =>
      buildRails({
        freeCollections: freeData,
        myHoldings: holdingsData,
        userProfile: profileData,
      }),
    [freeData, holdingsData, profileData]
  );

  const rails = useMemo(() => {
    const base = Array.isArray(railsBase) ? [...railsBase] : [];
    const items = Array.isArray(studioItems) ? studioItems : [];
    if (items.length > 0) {
      const studioRail = { title: "My Studio", items };
      const purchasesIdx = base.findIndex(
        (r) => typeof r?.title === "string" && r.title.trim().toLowerCase() === "my purchases"
      );
      if (purchasesIdx >= 0) base.splice(purchasesIdx + 1, 0, studioRail);
      else base.unshift(studioRail);
    }
    return base;
  }, [railsBase, studioItems]);

  const DEBUG_RAILS =
    String(process.env.REACT_APP_DEBUG_RAILS || "").toLowerCase() === "true" ||
    (typeof window !== "undefined" && window.__KBEAN_DEBUG_RAILS === true);
  const DEBUG_API = isDebugApi();

  useEffect(() => {
    if (!DEBUG_RAILS) return;
    logOnce("rails-summary", () => {
      console.groupCollapsed("[HomeScene] rails memo");
      try {
        const summary = (rails || []).map((r, i) => ({
          idx: i,
          title: r?.title,
          count: Array.isArray(r?.items) ? r.items.length : 0,
        }));
        console.table(summary);
      } finally {
        console.groupEnd();
      }
    });
  }, [rails, DEBUG_RAILS]);

  useEffect(() => {
    if (!DEBUG_API) return;
    logOnce("raw-slices", () => {
      console.groupCollapsed("[HomeScene] raw API slices from store");
      try {
        const freeCollectionsCount = Array.isArray(freeData) ? freeData.length : 0;
        const firstCollection = Array.isArray(freeData) ? freeData[0] : null;
        const firstArtwork = firstCollection?.artworksList?.[0] ?? null;

        console.log("freeData collections:", freeCollectionsCount);
        if (firstCollection) {
          console.log("freeData[0] keys:", Object.keys(firstCollection));
          console.log(
            "freeData[0].artworksList length:",
            Array.isArray(firstCollection.artworksList) ? firstCollection.artworksList.length : 0
          );
        }
        if (firstArtwork) {
          console.log("freeData[0].artworksList[0] keys:", Object.keys(firstArtwork));
        }
        console.log("holdingsData type:", Array.isArray(holdingsData) ? "array" : typeof holdingsData);
        console.log("profileData keys:", profileData ? Object.keys(profileData) : "(none)");
      } finally {
        console.groupEnd();
      }
    });
  }, [freeData, holdingsData, profileData, DEBUG_API]);

  useKeepScreenOn(false);

  const CARD_H = 210;
  const TITLE_H = 48;
  const ROW_GAP = 56;
  const ROW_STRIDE = CARD_H + TITLE_H + ROW_GAP;
  const RAILS_VIEW_HEIGHT = ROW_STRIDE * 2 + 16;

  const rowCount = rails?.length ?? 0;

  const [animateRows, setAnimateRows] = useState(false);
  const alignedOnceRef = useRef(false);
  const seededInitialFocusRef = useRef(false);

  const forceRow0Col0 = scene?.params?.forceRow0Col0 === true;

  const initialRow = useMemo(() => (forceRow0Col0 ? 0 : getSafeRow(rowCount)), [rowCount, forceRow0Col0]);
  const initialCol = useMemo(
    () => (forceRow0Col0 ? 0 : getSafeColForRow(rails, initialRow)),
    [rails, initialRow, forceRow0Col0]
  );

  const [focusedRow, setFocusedRow] = useState(initialRow);
  const [active, setActive] = useState(
    rails?.[initialRow]?.items?.[initialCol] ?? rails?.[0]?.items?.[0] ?? null
  );

  const focusedRowRef = useRef(focusedRow);
  const activeIdRef = useRef(active?.id ?? null);
  useEffect(() => {
    focusedRowRef.current = focusedRow;
  }, [focusedRow]);
  useEffect(() => {
    activeIdRef.current = active?.id ?? null;
  }, [active]);

  useEffect(() => {
    if (!DEBUG_RAILS || !active) return;
    logOnce(`active-${active?.id || "none"}`, () => {
      console.groupCollapsed("[HomeScene] active hero item");
      console.log({
        id: active?.id,
        title: pickTitle(active),
        subtitle: pickSubtitle(active),
        expectedMediaType: pickMediaType(active),
        backgroundUrl: pickBackground(active),
      });
      console.groupEnd();
    });
  }, [active, DEBUG_RAILS]);

  useEffect(() => {
    setSceneHandlers({
      onBack: () => {
        window.dispatchEvent(new CustomEvent("request-exit-app"));
        return true;
      },
    });
  }, []);

  const goItem = (item) => {
    if (item?.type === "collection") {
      setScene({
        name: "grid",
        params: {
          source: "collectionItems",
          title: item?.title || "Collection",
          collectionId: item?.id,
          backTo: { name: "home", params: { openSideNav: true, focusNavKey: "collections" } },
          sceneKey: `grid-collection-${item?.id}`,
        },
      });
      return;
    }

    // Enrich payload so ItemDetailScene has full hero metadata parity
    const payload = normalizeForDetail(item);

    if (isDebugItem()) {
      console.groupCollapsed("[Nav] goItem → payload to ItemDetailScene");
      try {
        console.log("keys:", Object.keys(payload || {}));
        console.log(payload);
        if (typeof window !== "undefined") {
          window.__KBEAN_LAST_ITEM_PAYLOAD = JSON.parse(JSON.stringify(payload));
        }
      } finally {
        console.groupEnd();
      }
    }

    (pushScene || setScene)({
      name: "item",
      params: { item: payload, id: payload?.id, backTo: { name: "home" } },
    });
  };

  useEffect(() => {
    if (scene?.params?.resetRowMemory) clearRowMemory();
  }, [scene?.params?.resetRowMemory]);

  useLayoutEffect(() => {
    if (scene?.name !== "home") return;
    if (rowCount === 0) return;

    const row = forceRow0Col0 ? 0 : getSafeRow(rowCount);
    const col = forceRow0Col0 ? 0 : getSafeColForRow(rails, row);
    const nextActive = rails?.[row]?.items?.[col] ?? null;

    if (!seededInitialFocusRef.current) {
      setAnimateRows(false);
      focusRowCol(row, col, { retryMs: 1200 });
      seededInitialFocusRef.current = true;
      requestAnimationFrame(() => {
        alignedOnceRef.current = true;
        setAnimateRows(true);
      });
    }

    if (row !== focusedRowRef.current) setFocusedRow(row);
    const nextId = nextActive?.id ?? null;
    if (nextId !== activeIdRef.current) setActive(nextActive);
  }, [scene?.name, rowCount, rails, forceRow0Col0]);

  const clampedFocusedRow = clamp(focusedRow, 0, Math.max(0, rowCount - 1));

  // ── Compute hero props from the active item ─────────────────────────────
  const heroTitle = active ? pickTitle(active) : HOME_HERO_DEFAULT.title;
  const heroSubtitle = active ? pickSubtitle(active) : "";

  const af = pickArtistFirst(active);
  const al = pickArtistLast(active);
  const yr = pickYear(active);
  const byline =
    af || al || yr
      ? [[af, al].filter(Boolean).join(" "), yr].filter(Boolean).join(" • ")
      : null;

  const medium = pickMedium(active);
  const expectedMediaType = pickMediaType(active);
  const isVideo = expectedMediaType === "Video";
  const shares = Number(pickShares(active) || 0);
  const value = pickValue(active);

  const badges = [isVideo ? "Video" : null].filter(Boolean);

  const ownershipLabel =
    shares > 0
      ? `You own ${shares} ${shares === 1 ? "share" : "shares"}${value ? ` ($${value})` : ""}`
      : undefined;

  const heroBackground = active ? pickBackground(active) : HOME_HERO_DEFAULT.backgroundUrl;

  return (
    <Box
      className="scene"
      sx={{
        height: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        p: 0,
      }}
      data-scene="home"
    >
      <Hero
        title={heroTitle}
        subtitle={heroSubtitle}
        backgroundUrl={heroBackground}
        gradientStops="linear-gradient(135deg, rgba(0,0,0,0.85) 12%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.2) 70%, rgba(0,0,0,0) 100%)"
        textTop="14vh"
        textLeft={NAV_SAFE_LEFT}
        byline={byline || undefined}
        meta={medium || undefined}
        badges={badges}
        ownershipLabel={ownershipLabel}
      />

      <SideNav
        defaultOpen={!!scene?.params?.openSideNav}
        focusKey={scene?.params?.focusNavKey || null}
      />

      <Box
        sx={{
          position: "relative",
          height: `${RAILS_VIEW_HEIGHT}px`,
          width: "100%",
          overflow: "hidden",
          mt: `calc(50vh + ${ANCHOR_EXTRA_OFFSET}px)`,
          zIndex: 900,
          contain: "layout paint",
          transform: "translateZ(0)",
        }}
        data-rails-root
        data-scope="rails"
      >
        {(rails || []).map((r, i) => {
          const distance = i - clampedFocusedRow;
          const initialLeadIndex = forceRow0Col0 ? 0 : getSafeColForRow(rails, i);

          return (
            <Box
              key={`${r.title}-${i}`}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                height: `${ROW_STRIDE}px`,
                transform: `translate3d(0, ${distance * ROW_STRIDE}px, 0)`,
                opacity: distance < 0 ? 0 : 1,
                transition: animateRows ? "transform 350ms ease, opacity 160ms ease" : "none",
                willChange: "transform, opacity",
              }}
            >
              <Box sx={{ ml: `${NAV_SAFE_LEFT}px`, pr: 0 }}>
                <CustomSliderRow
                  title={r.title}
                  items={r.items}
                  rowIndex={i}
                  onEnterItem={goItem}
                  onFocusItem={(item, meta) => {
                    if ((active?.id || null) !== (item?.id || null)) setActive(item);
                    if (typeof meta?.rowIndex === "number" && meta.rowIndex !== focusedRow) {
                      setFocusedRow(clamp(meta.rowIndex, 0, Math.max(0, rowCount - 1)));
                    }
                  }}
                  edgePeekPx={60}
                  gapPx={16}
                  cardWidth={372}
                  cardHeight={210}
                  titleHeight={48}
                  titlePaddingBottom={8}
                  titleVariant="h2"
                  titleColor="text.primary"
                  fadeLeftOpacity={1}
                  fadeRightOpacity={1}
                  initialLeadIndex={initialLeadIndex}
                  animate={animateRows}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
